import { useState, useEffect } from 'react';
import { reportsApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { format, subDays } from 'date-fns';

export default function Reports() {
  const defaultStart = format(subDays(new Date(), 30), 'yyyy-MM-dd');
  const defaultEnd = format(new Date(), 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [tab, setTab] = useState('pl');

  // Filter states      
  const [selectedTailor, setSelectedTailor] = useState('');
  const [selectedLot, setSelectedLot] = useState('');
  const [selectedLotOrders, setSelectedLotOrders] = useState('');

  // Lot Orders date filter states 
  const [lotOrderStartDate, setLotOrderStartDate] = useState('');
  const [lotOrderEndDate, setLotOrderEndDate] = useState('');
  const [lotOrderApplyTrigger, setLotOrderApplyTrigger] = useState(0);

  // Tailor filter states (for Tailor Performance tab)
  const [tailorYear, setTailorYear] = useState('');
  const [tailorMonth, setTailorMonth] = useState('');

  // Profit tab filter states
  const [profitStartDate, setProfitStartDate] = useState('');
  const [profitEndDate, setProfitEndDate] = useState('');
  const [profitData, setProfitData] = useState(null);
  const [profitLoading, setProfitLoading] = useState(false);

  // Fetch filter options
  const { data: filterOptions } = useFetch(() => reportsApi.getFilterOptions(), []);
  const tailors = filterOptions?.tailors || [];
  const lots = filterOptions?.lots || [];

  const { data: plData, loading: plLoading, refetch: refetchPl } = useFetch(() => reportsApi.getProfitLoss({ startDate, endDate }), [startDate, endDate]);
  const { data: tailorData, loading: tLoading, refetch: refetchTailor } = useFetch(() => reportsApi.getTailorPerformance({ startDate, endDate, tailorId: selectedTailor || undefined, year: tailorYear || undefined, month: tailorMonth || undefined }), [startDate, endDate, selectedTailor, tailorYear, tailorMonth]);
  const { data: invData, loading: iLoading, refetch: refetchInv } = useFetch(() => reportsApi.getInventoryReport({ startDate, endDate, lotId: selectedLot || undefined }), [startDate, endDate, selectedLot]);

  // Custom fetch for lot orders with proper null handling
  const [lotOrdersData, setLotOrdersData] = useState(null);
  const [lotOrdersLoading, setLotOrdersLoading] = useState(false);

  useEffect(() => {
    if (!selectedLotOrders) {
      setLotOrdersData(null);
      return;
    }

    const fetchLotOrders = async () => {
      setLotOrdersLoading(true);
      try {
        const params = { lotId: selectedLotOrders };
        if (lotOrderStartDate) params.startDate = lotOrderStartDate;
        if (lotOrderEndDate) params.endDate = lotOrderEndDate;
        const res = await reportsApi.getOrdersByLot(params);
        setLotOrdersData(res.data.data);
      } catch (err) {
        console.error('Error fetching lot orders:', err);
        setLotOrdersData(null);
      } finally {
        setLotOrdersLoading(false);
      }
    };

    fetchLotOrders();
  }, [selectedLotOrders, lotOrderStartDate, lotOrderEndDate, lotOrderApplyTrigger]);

  // Fetch profit data
  useEffect(() => {
    if (tab === 'Profit') {
      setProfitLoading(true);
      const params = {};

      if (profitStartDate && profitEndDate) {
        params.startDate = profitStartDate;
        params.endDate = profitEndDate;
      } else {
        const today = new Date();
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(today.getDate() - 30);
        params.startDate = thirtyDaysAgo.toISOString().split('T')[0];
        params.endDate = today.toISOString().split('T')[0];
      }

      reportsApi.getMonthlyProfit(params)
        .then(res => setProfitData(res.data.data))
        .catch(err => { console.error('Error fetching profit:', err); setProfitData(null); })
        .finally(() => setProfitLoading(false));
    }
  }, [tab, profitStartDate, profitEndDate]);

  const daily = plData?.daily || [];
  const totals = plData?.totals || {};
  const tailorsData = tailorData || [];
  
  const displayTotals = {
    totalOrders: parseInt(totals.totalOrders) || 0,
    totalRevenue: parseFloat(totals.totalRevenue) || 0,
    totalCost: parseFloat(totals.totalCost) || 0,
    totalProfit: parseFloat(totals.totalProfit) || 0,
  };

  const applyDateFilter = () => {
    refetchPl();
    refetchTailor();
    refetchInv();
  };

  const applyFilters = () => {
    refetchTailor();
    refetchInv();
  };

  // Handle filter changes
  const handleTailorChange = (e) => {
    setSelectedTailor(e.target.value);
    refetchTailor();
  };

  const handleLotChange = (e) => {
    setSelectedLot(e.target.value);
    refetchInv();
  };

  const handleLotOrdersChange = (e) => {
    setSelectedLotOrders(e.target.value);
  };

  const clearFilters = () => {
    setSelectedTailor('');
    setSelectedLot('');
    setSelectedLotOrders('');
    setLotOrderStartDate('');
    setLotOrderEndDate('');
    setTailorYear('');
    setTailorMonth('');
    setProfitStartDate('');
    setProfitEndDate('');
    refetchTailor();
    refetchInv();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <div className="flex items-center gap-3">
          <input type="date" className="form-control" style={{ width: 150 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <span className="text-muted">to</span>
          <input type="date" className="form-control" style={{ width: 150 }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <button className="btn btn-primary" onClick={applyDateFilter}>Apply</button>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {[['pl', 'P&L Report'], ['tailor', 'Tailor Performance'], ['inventory', 'Inventory'], ['lotOrders', 'Lot Orders'], ['Profit', 'Profit']].map(([k, l]) => (
          <button key={k} className={`btn ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'pl' && (
        <>
          {/* Summary Cards */}
          <div className="grid-4 mb-6">
            {[
              { label: 'Total Orders', value: displayTotals.totalOrders || 0, color: '#6366f1' },
              { label: 'Total Revenue', value: `₹${Number(displayTotals.totalRevenue || 0).toLocaleString('en-IN')}`, color: '#3b82f6' },
              { label: 'Total Cost', value: `₹${Number(displayTotals.totalCost || 0).toLocaleString('en-IN')}`, color: '#f59e0b' },
              { label: 'Net Profit', value: `₹${Number(displayTotals.totalProfit || 0).toLocaleString('en-IN')}`, color: '#10b981' },
            ].map((s) => (
              <div key={s.label} className="stat-card" style={{ '--card-accent': s.color }}>
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="card mb-6">
            <div className="card-header"><h3>Daily Revenue vs Profit</h3></div>
            <div className="card-body" style={{ height: 320 }}>
              {plLoading ? <div className="empty-state"><div className="spinner" /></div> : daily.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(d) => format(new Date(d), 'dd MMM')} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #2a3a54', borderRadius: 8 }} formatter={(v, n) => [`₹${Number(v).toLocaleString('en-IN')}`, n === 'revenue' ? 'Revenue' : n === 'cost' ? 'Cost' : 'Profit']} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} name="revenue" />
                    <Bar dataKey="cost" fill="#f59e0b" radius={[4, 4, 0, 0]} name="cost" />
                    <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} name="profit" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="empty-state"><div className="empty-icon">📊</div><p>No delivered orders in this period</p></div>}
            </div>
          </div>
        </>
      )}

      {tab === 'tailor' && (
        <div className="card">
          {/* Tailor Filter */}
          <div className="flex items-center gap-3 mb-4" style={{ padding: '1rem', background: '#1e293b', borderRadius: '8px', marginBottom: '1rem' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">Filter by Tailor:</label>
              <select
                className="form-control"
                style={{ width: 200 }}
                value={selectedTailor}
                onChange={handleTailorChange}
              >
                <option value="">All Tailors</option>
                {tailors.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Year Filter for Tailor */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">Year:</label>
              <select
                className="form-control"
                style={{ width: 120 }}
                value={tailorYear}
                onChange={(e) => { setTailorYear(e.target.value); setTailorMonth(''); }}
              >
                <option value="">All Years</option>
                {[2024, 2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Month Filter for Tailor */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">Month:</label>
              <select
                className="form-control"
                style={{ width: 150 }}
                value={tailorMonth}
                onChange={(e) => { setTailorMonth(e.target.value); }}
                disabled={!tailorYear}
              >
                <option value="">All Months</option>
                {[
                  { value: 1, label: 'January' },
                  { value: 2, label: 'February' },
                  { value: 3, label: 'March' },
                  { value: 4, label: 'April' },
                  { value: 5, label: 'May' },
                  { value: 6, label: 'June' },
                  { value: 7, label: 'July' },
                  { value: 8, label: 'August' },
                  { value: 9, label: 'September' },
                  { value: 10, label: 'October' },
                  { value: 11, label: 'November' },
                  { value: 12, label: 'December' },
                ].map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {(selectedTailor || tailorYear || tailorMonth) && (
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear Filter
              </button>
            )}
            <span className="text-sm text-muted">
              {selectedTailor ? `Showing: ${tailors.find(t => t.id === parseInt(selectedTailor))?.name}` : 'Showing all tailors'}
              {tailorYear && tailorMonth ? ` | ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][tailorMonth - 1]} ${tailorYear}` : tailorYear ? ` | ${tailorYear}` : ''}
            </span>
          </div>

          {tLoading ? <div className="empty-state"><div className="spinner" /></div> : (
            <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
              <table>
                <thead><tr><th>Tailor</th><th>Total Assigned</th><th>Completed</th><th>Completion %</th><th>Total Earned</th><th>Avg Time (hrs)</th></tr></thead>
                <tbody>
                  {tailorsData.length === 0 ? (
                    <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">✂️</div><p>No tailor data</p></div></td></tr>
                  ) : tailorsData.map((t) => {
                    const rate = t.totalAssignments > 0 ? ((t.completedCount / t.totalAssignments) * 100).toFixed(0) : 0;
                    return (
                      <tr key={t.tailorId}>
                        <td>
                          <div className="font-semibold">{t.tailor?.name}</div>
                          <div className="text-xs text-muted">{t.tailor?.phone}</div>
                        </td>
                        <td className="font-semibold">{t.totalAssignments}</td>
                        <td className="text-success font-semibold">{t.completedCount}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="progress-bar" style={{ width: 80 }}><div className="progress-bar-fill" style={{ width: `${rate}%` }} /></div>
                            <span className="text-sm">{rate}%</span>
                          </div>
                        </td>
                        <td className="font-semibold text-success">₹{Number(t.totalEarned || 0).toLocaleString('en-IN')}</td>
                        <td className="text-muted">{t.avgCompletionHours ? Number(t.avgCompletionHours).toFixed(1) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'inventory' && (
        <>
          <div className="grid-3 mb-6">
            <div className="stat-card"><div className="stat-value" style={{ color: '#3b82f6' }}>₹{Number(invData?.clothValue || 0).toLocaleString('en-IN')}</div><div className="stat-label">Cloth Inventory Value</div></div>
            <div className="stat-card"><div className="stat-value" style={{ color: '#8b5cf6' }}>₹{Number(invData?.accessoryValue || 0).toLocaleString('en-IN')}</div><div className="stat-label">Accessory Inventory Value</div></div>
            <div className="stat-card"><div className="stat-value" style={{ color: '#10b981' }}>₹{Number(invData?.totalValue || 0).toLocaleString('en-IN')}</div><div className="stat-label">Total Inventory Value</div></div>
          </div>

          {/* Lot Filter */}
          <div className="flex items-center gap-3 mb-4" style={{ padding: '1rem', background: '#1e293b', borderRadius: '8px', marginBottom: '1rem' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">Filter by Lot:</label>
              <select
                className="form-control"
                style={{ width: 200 }}
                value={selectedLot}
                onChange={handleLotChange}
              >
                <option value="">All Lots</option>
                {lots.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.lotNumber} - {l.fabricType} ({l.color})
                  </option>
                ))}
              </select>
            </div>
            {(selectedLot) && (
              <button className="btn btn-secondary" onClick={clearFilters}>
                Clear Filter
              </button>
            )}
            <span className="text-sm text-muted">
              {selectedLot ? `Showing: ${lots.find(l => l.id === parseInt(selectedLot))?.lotNumber}` : 'Showing all lots'}
            </span>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-header"><h3>Cloth Inventory</h3></div>
              <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                <table>
                  <thead><tr><th>Lot</th><th>Type</th><th>Available</th><th>Value</th></tr></thead>
                  <tbody>
                    {iLoading ? <tr><td colSpan={4}><div className="empty-state"><div className="spinner" /></div></td></tr>
                      : (invData?.cloth || []).map((inv) => (
                        <tr key={inv.id}>
                          <td className="mono text-xs">{inv.lot?.lotNumber}</td>
                          <td><span className="badge badge-pending">{inv.clothType?.replace('_', ' ')}</span></td>
                          <td className={Number(inv.quantityAvailable) <= Number(inv.lowStockThreshold) ? 'text-danger font-semibold' : 'font-semibold'}>{Number(inv.quantityAvailable).toFixed(2)}</td>
                          <td>₹{(Number(inv.quantityAvailable) * Number(inv.lot?.purchasePricePerUnit || 0)).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header"><h3>Accessory Inventory</h3></div>
              <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                <table>
                  <thead><tr><th>Name</th><th>Category</th><th>Available</th><th>Value</th></tr></thead>
                  <tbody>
                    {iLoading ? <tr><td colSpan={4}><div className="empty-state"><div className="spinner" /></div></td></tr>
                      : (invData?.accessories || []).map((a) => (
                        <tr key={a.id}>
                          <td className="font-semibold text-sm">{a.name}</td>
                          <td><span className="badge badge-stitching">{a.category}</span></td>
                          <td className={Number(a.quantityAvailable) <= Number(a.lowStockThreshold) ? 'text-danger font-semibold' : 'font-semibold'}>{Number(a.quantityAvailable).toFixed(2)}</td>
                          <td>₹{(Number(a.quantityAvailable) * Number(a.costPerUnit)).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'lotOrders' && (
        <>
          {/* Lot Selection with Date Filter */}
          <div className="flex items-center gap-3 mb-4" style={{ padding: '1rem', background: '#1e293b', borderRadius: '8px', marginBottom: '1rem' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">Select Lot:</label>
              <select
                className="form-control"
                style={{ width: 250 }}
                value={selectedLotOrders}
                onChange={handleLotOrdersChange}
              >
                <option value="">-- Select Lot --</option>
                {lots.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.lotNumber} - {l.fabricType} ({l.color})
                  </option>
                ))}
              </select>
            </div>

            {selectedLotOrders && (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted">From:</label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ width: 150 }}
                    value={lotOrderStartDate}
                    onChange={(e) => setLotOrderStartDate(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted">To:</label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ width: 150 }}
                    value={lotOrderEndDate}
                    onChange={(e) => setLotOrderEndDate(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary" onClick={() => {
                  setLotOrderApplyTrigger(prev => prev + 1);
                }}>Apply</button>
              </>
            )}

            {(selectedLotOrders) && (
              <button className="btn btn-secondary" onClick={() => { setSelectedLotOrders(''); setLotOrderStartDate(''); setLotOrderEndDate(''); setLotOrderApplyTrigger(0); }}>
                Clear
              </button>
            )}
          </div>

          {selectedLotOrders ? (
            lotOrdersLoading ? (
              <div className="empty-state"><div className="spinner" /></div>
            ) : lotOrdersData ? (
              <>
                {/* Summary Cards */}
                <div className="grid-3 mb-6">
                  <div className="stat-card" style={{ '--card-accent': '#6366f1' }}>
                    <div className="stat-value" style={{ color: '#6366f1' }}>{lotOrdersData.totalOrders || 0}</div>
                    <div className="stat-label">Total Orders</div>
                  </div>
                  <div className="stat-card" style={{ '--card-accent': '#3b82f6' }}>
                    <div className="stat-value" style={{ color: '#3b82f6' }}>₹{Number(lotOrdersData.totals?.totalRevenue || 0).toLocaleString('en-IN')}</div>
                    <div className="stat-label">Total Revenue</div>
                  </div>
                  <div className="stat-card" style={{ '--card-accent': '#10b981' }}>
                    <div className="stat-value" style={{ color: '#10b981' }}>₹{Number(lotOrdersData.totals?.totalProfit || 0).toLocaleString('en-IN')}</div>
                    <div className="stat-label">Total Profit</div>
                  </div>
                </div>

                {/* Lot Info */}
                <div className="card mb-6">
                  <div className="card-header">
                    <h3>Lot Details: {lotOrdersData.lot?.lotNumber}</h3>
                  </div>
                  <div className="card-body">
                    <div className="grid-4">
                      <div>
                        <div className="text-xs text-muted">Fabric Type</div>
                        <div className="font-semibold">{lotOrdersData.lot?.fabricType}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted">Color</div>
                        <div className="font-semibold">{lotOrdersData.lot?.color}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted">Purchase Price</div>
                        <div className="font-semibold">₹{Number(lotOrdersData.lot?.purchasePricePerUnit || 0).toLocaleString('en-IN')}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted">Purchased On</div>
                        <div className="font-semibold">{lotOrdersData.lot?.purchasedAt ? format(new Date(lotOrdersData.lot.purchasedAt), 'dd MMM yyyy') : '-'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly Breakdown Chart */}
                <div className="card mb-6">
                  <div className="card-header"><h3>Monthly Orders Breakdown</h3></div>
                  <div className="card-body" style={{ height: 320 }}>
                    {lotOrdersData.monthlyData && lotOrdersData.monthlyData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={lotOrdersData.monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(d) => {
                            const [year, month] = d.split('-');
                            return format(new Date(year, parseInt(month) - 1), 'MMM yy');
                          }} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 11 }} />
                          <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #2a3a54', borderRadius: 8 }} formatter={(v, n) => {
                            if (n === 'orderCount') return [v, 'Orders'];
                            if (n === 'revenue') return [`₹${Number(v).toLocaleString('en-IN')}`, 'Revenue'];
                            if (n === 'profit') return [`₹${Number(v).toLocaleString('en-IN')}`, 'Profit'];
                            return [v, n];
                          }} />
                          <Legend />
                          <Bar dataKey="orderCount" fill="#6366f1" radius={[4, 4, 0, 0]} name="orderCount" />
                          <Bar dataKey="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} name="revenue" />
                          <Bar dataKey="profit" fill="#10b981" radius={[4, 4, 0, 0]} name="profit" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="empty-state"><div className="empty-icon">📊</div><p>No orders found for this lot</p></div>
                    )}
                  </div>
                </div>

                {/* Monthly Data Table */}
                <div className="card mb-6">
                  <div className="card-header"><h3>Monthly Summary</h3></div>
                  <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                    <table>
                      <thead><tr><th>Month</th><th>Orders</th><th>Revenue</th><th>Profit</th></tr></thead>
                      <tbody>
                        {lotOrdersData.monthlyData && lotOrdersData.monthlyData.length > 0 ? (
                          lotOrdersData.monthlyData.map((m) => (
                            <tr key={m.month}>
                              <td className="font-semibold">{format(new Date(m.month + '-01'), 'MMMM yyyy')}</td>
                              <td className="font-semibold">{m.orderCount}</td>
                              <td className="text-success">₹{Number(m.revenue || 0).toLocaleString('en-IN')}</td>
                              <td className="text-primary">₹{Number(m.profit || 0).toLocaleString('en-IN')}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={4}><div className="empty-state"><div className="empty-icon">📋</div><p>No monthly data available</p></div></td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="card">
                  <div className="card-header"><h3>Recent Orders</h3></div>
                  <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                    <table>
                      <thead><tr><th>Order #</th><th>Customer</th><th>Phone</th><th>Status</th><th>Total</th><th>Date</th></tr></thead>
                      <tbody>
                        {lotOrdersData.orders && lotOrdersData.orders.length > 0 ? (
                          lotOrdersData.orders.map((o) => (
                            <tr key={o.id}>
                              <td className="mono text-xs">{o.orderNumber}</td>
                              <td className="font-semibold">{o.customer?.name}</td>
                              <td className="text-muted">{o.customer?.phone}</td>
                              <td><span className={`badge badge-${o.status === 'delivered' ? 'success' : o.status === 'cancelled' ? 'danger' : 'pending'}`}>{o.status}</span></td>
                              <td className="font-semibold text-success">₹{Number(o.totalSellingPrice || 0).toLocaleString('en-IN')}</td>
                              <td className="text-muted">{format(new Date(o.createdAt), 'dd MMM yyyy')}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">📦</div><p>No orders found</p></div></td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state"><div className="empty-icon">⚠️</div><p>Error loading lot orders data</p></div>
            )
          ) : (
            <div className="card">
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <p>Select a lot to view orders</p>
                <p className="text-sm text-muted mt-2">Choose a lot from the dropdown above to see how many orders were created and their monthly breakdown.</p>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'Profit' && (
        <>
          <div className="flex items-center gap-3 mb-6" style={{ padding: '1rem', background: '#1e293b', borderRadius: '8px' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">From:</label>
              <input
                type="date"
                className="form-control"
                style={{ width: 150 }}
                value={profitStartDate}
                onChange={(e) => setProfitStartDate(e.target.value)}
              />
            </div>
            <span className="text-muted">to</span>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted">To:</label>
              <input
                type="date"
                className="form-control"
                style={{ width: 150 }}
                value={profitEndDate}
                onChange={(e) => setProfitEndDate(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                if (!profitStartDate || !profitEndDate) {
                  const today = new Date();
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setProfitStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
                  setProfitEndDate(today.toISOString().split('T')[0]);
                }
              }}
            >
              Apply
            </button>
          </div>

          {profitLoading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : profitData ? (
            <>
              <div className="grid-4 mb-6">
                <div className="stat-card" style={{ '--card-accent': '#6366f1' }}>
                  <div className="stat-value" style={{ color: '#6366f1' }}>{profitData.totalOrders || 0}</div>
                  <div className="stat-label">Total Orders</div>
                </div>
                <div className="stat-card" style={{ '--card-accent': '#3b82f6' }}>
                  <div className="stat-value" style={{ color: '#3b82f6' }}>₹{Number(profitData.totals?.totalRevenue || 0).toLocaleString('en-IN')}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
                <div className="stat-card" style={{ '--card-accent': '#f59e0b' }}>
                  <div className="stat-value" style={{ color: '#f59e0b' }}>₹{Number(profitData.totals?.totalCost || 0).toLocaleString('en-IN')}</div>
                  <div className="stat-label">Total Cost</div>
                </div>
                <div className="stat-card" style={{ '--card-accent': '#10b981' }}>
                  <div className="stat-value" style={{ color: '#10b981' }}>₹{Number(profitData.totals?.totalProfit || 0).toLocaleString('en-IN')}</div>
                  <div className="stat-label">Total Profit</div>
                </div>
              </div>

              {profitData.lotBreakdown && profitData.lotBreakdown.length > 0 && (
                <div className="card mb-6">
                  <div className="card-header"><h3>Lot-wise Profit Breakdown</h3></div>
                  <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                    <table>
                      <thead><tr><th>Lot Number</th><th>Fabric Type</th><th>Color</th><th>Quantity Used</th><th>Cost</th><th>Revenue</th><th>Profit</th><th>Orders</th></tr></thead>
                      <tbody>
                        {profitData.lotBreakdown.map((lot, idx) => (
                          <tr key={idx}>
                            <td className="mono text-sm">{lot.lot?.lotNumber}</td>
                            <td>{lot.lot?.fabricType}</td>
                            <td>{lot.lot?.color}</td>
                            <td className="font-semibold">{Number(lot.totalQuantity).toFixed(2)}</td>
                            <td className="text-warning">₹{Number(lot.totalCost).toLocaleString('en-IN')}</td>
                            <td className="text-primary">₹{Number(lot.totalRevenue).toLocaleString('en-IN')}</td>
                            <td className="text-success font-semibold">₹{Number(lot.totalProfit).toLocaleString('en-IN')}</td>
                            <td className="font-semibold">{lot.orderCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="card">
                <div className="card-header"><h3>Orders in {profitData.periodLabel || profitData.monthName + ' ' + profitData.year}</h3></div>
                <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                  <table>
                    <thead><tr><th>Order #</th><th>Customer</th><th>Phone</th><th>Cost</th><th>Selling Price</th><th>Profit</th><th>Status</th></tr></thead>
                    <tbody>
                      {profitData.orders && profitData.orders.length > 0 ? (
                        profitData.orders.map((order) => (
                          <tr key={order.id}>
                            <td className="mono text-sm">{order.orderNumber}</td>
                            <td className="font-semibold">{order.customer?.name}</td>
                            <td className="text-muted">{order.customer?.phone}</td>
                            <td className="text-warning">₹{Number(order.totalCost).toLocaleString('en-IN')}</td>
                            <td className="text-primary">₹{Number(order.totalSellingPrice).toLocaleString('en-IN')}</td>
                            <td className="text-success font-semibold">₹{Number(order.profit).toLocaleString('en-IN')}</td>
                            <td><span className={`badge badge-${order.status === 'delivered' ? 'success' : 'pending'}`}>{order.status}</span></td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={7}><div className="empty-state"><div className="empty-icon">📋</div><p>No delivered orders in this month</p></div></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="card">
              <div className="empty-state"><div className="empty-icon">📊</div><p>Select a month to view profit report</p></div>
            </div>
          )}
        </>
      )}

    </div>
  );
}
